import Job from 'jobs/Job'
import {
  JobProgressionFlow,
  JobProgressionStepStatus,
  OverrideableJobProgressionStepStatus,
} from 'jobs/progression/JobProgression'
import JobProgressionStore from 'jobs/progression/JobProgressionStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { match } from 'utils/pattern-match-util'
import { StartProgressionDialogViewModel } from './NoProgression/StartProgressionDialog/StartProgressionDialogViewModel'
import { DocumentCreationModalViewModel } from 'documents/views/DocumentCreationModal/DocumentCreationModalViewModel'
import Document from 'documents/Document'
import { WellKnownDocumentTypeShortId } from 'documents/dtos/document-type'
import { OpenItemsDialogViewModel } from './OpenItems/OpenItemsDialog/OpenItemsDialogViewModel'

export default class JobProgressionViewModel {
  constructor(
    private progressionStore: JobProgressionStore,
    private requestJobClose: () => void,
    private createDocumentCreationModalViewModel: () => DocumentCreationModalViewModel,
    private getJobDocuments: () => Document[],
    public job: Job
  ) {
    makeObservable(this)
  }

  @observable
  returnFiledDocumentCreationVM: DocumentCreationModalViewModel | null = null

  activate = task.resolved(async () => {
    await this.progressionStore.fetchProgression(this.job.id)
  })

  deactivate() {
    this.activate.reset()
  }

  /**
   * Dialog for setting job progression to job started.
   */
  startProgressionDialogVM = new StartProgressionDialogViewModel(
    this.progressionStore,
    this.job
  )

  /**
   * Dialog for settings job progression to open items
   */
  openItemsDialogVM = new OpenItemsDialogViewModel(
    this.progressionStore,
    this.job
  )

  /**
   * Progression steps for the current job.
   */
  @computed
  get progression() {
    return this.progressionStore?.progressionForJob(this.job.id)
  }

  /**
   * The current progression step status.
   */
  @computed
  get currentStepStatus() {
    return (
      this.progression?.steps?.find((s) => s.response === null)?.status ??
      JobProgressionStepStatus.NOT_STARTED
    )
  }

  /*
   * Whether the job progression flow has been started or not.
   */
  @computed
  get hasStarted() {
    return this.currentStepStatus !== JobProgressionStepStatus.NOT_STARTED
  }

  /**
   * Is current step status DRAFT_APPROVED
   */
  @computed
  get isCurrentStepStatusDraftApproved() {
    return this.currentStepStatus === JobProgressionStepStatus.DRAFT_APPROVED
  }

  /**
   * The current percentage of the progression flow completed.
   */
  @computed
  get completionStatus() {
    return this.job.completed
      ? 100
      : this.currentStepInfo?.progress.completed ?? 0
  }

  /**
   * The current percentage of the progression flow pending completion.
   */
  @computed
  get pendingCompletionStatus() {
    return this.job.completed ? 0 : this.currentStepInfo?.progress.pending ?? 0
  }

  /**
   * The current progression flow.
   */
  @computed
  get currentProgressionFlow() {
    return this.progression?.progressionFlow ?? JobProgressionFlow.NONE
  }

  /**
   * Returns details for the current progression step.
   */
  @computed
  get currentStepInfo() {
    return match(this.currentProgressionFlow, {
      [JobProgressionFlow.OUTSOURCING]: () =>
        outsourcingProgressionStepsDetails[this.currentStepStatus],
      [JobProgressionFlow.RETAIL]: () =>
        retailProgressionStepsDetails[this.currentStepStatus],
      [JobProgressionFlow.NONE]: () => undefined,
    })
  }

  @computed
  get isUploadFinalReturnDisabled() {
    if (!this.returnFiledDocumentCreationVM) {
      return true
    }

    // If you can skip upload, enable upload button only if the Pro has uploaded documents.
    if (!this.isSkipUploadFinalReturnDisabled) {
      return !this.returnFiledDocumentCreationVM.documentCreationItemVMs.length
    }

    return !this.returnFiledDocumentCreationVM.documentCreationItemVMs
      .map((i) => i.selectedType?.shortId)
      .some(
        (shortId) =>
          shortId === WellKnownDocumentTypeShortId.FinalReturn ||
          shortId === WellKnownDocumentTypeShortId.ExtensionConfirmation
      )
  }

  @computed
  get isSkipUploadFinalReturnDisabled() {
    const jobDocuments = this.getJobDocuments()

    return !jobDocuments
      .map((d) => d.documentType?.shortId)
      .some(
        (shortId) =>
          shortId === WellKnownDocumentTypeShortId.FinalReturn ||
          shortId === WellKnownDocumentTypeShortId.ExtensionConfirmation
      )
  }

  @computed
  get overrideableProgressionSteps(): [
    OverrideableJobProgressionStepStatus,
    ProgressionStepDetails
  ][] {
    return match(this.currentProgressionFlow, {
      [JobProgressionFlow.RETAIL]: () => [
        [
          OverrideableJobProgressionStepStatus.RETURN_FILED,
          retailProgressionStepsDetails[JobProgressionStepStatus.RETURN_FILED]!,
        ],
      ],
      [JobProgressionFlow.OUTSOURCING]: () => [],
      [JobProgressionFlow.NONE]: () => [],
    })
  }

  /**
   * Sets the current progression to job started.
   */
  answerJobStarted = task.resolved(async () => {
    await this.progressionStore.setJobStartedProgression(this.job.id)
  })

  /**
   * Shows the documents received dialog.
   */
  showJobStartedDialog() {
    this.startProgressionDialogVM.show()
  }

  /**
   * Sets the current progression to draft in review.
   */
  answerDraftInReview = task.resolved(async () => {
    await this.progressionStore.setDraftInReviewProgression(this.job.id)
  })

  /**
   * Sets the current progression to authorization requested.
   */
  answerAuthorizationRequested = task.resolved(async () => {
    await this.progressionStore.setAuthorizationRequestedProgression(
      this.job.id
    )
  })

  /**
   * Sets the current progression to authorization signed.
   */
  answerAuthorizationSigned = task.resolved(async () => {
    await this.progressionStore.setAuthorizationSignedProgression(this.job.id)
  })

  /**
   * Sets the current progression to return filed.
   */
  answerReturnFiled = task.resolved(async (skipDocumentUpload: boolean) => {
    if (!this.returnFiledDocumentCreationVM) {
      return
    }

    if (!skipDocumentUpload) {
      await this.returnFiledDocumentCreationVM.uploadFiles()
    }

    await this.progressionStore.setReturnFiledProgression(this.job.id)
    this.returnFiledDocumentCreationVM.close()
  })

  /**
   * Overrides the current progression to specific step.
   */
  overrideProgressionStep = task.resolved(
    async (stepId: OverrideableJobProgressionStepStatus) => {
      await this.progressionStore.overrideProgressionStep(this.job.id, stepId)
    }
  )

  /**
   * Displays a modal to close out the current job.
   */
  @action.bound
  closeJob() {
    this.requestJobClose()
  }

  @action.bound
  viewReturnFiledDocumentCreationModal() {
    this.returnFiledDocumentCreationVM =
      this.createDocumentCreationModalViewModel()
    this.returnFiledDocumentCreationVM.show()
  }
}

/**
 * Tracks the job progression completion at a step
 */
export interface ProgressionCompletion {
  completed: number
  pending: number
}

/**
 * Progression step details for populating the progression card UI.
 */
export interface StaticProgressionStepDetails {
  title: string
  description: string
  progress: ProgressionCompletion
}

interface ProgressionStepDetails {
  title: string
  proDescription: string
  clientDescription: string
  progress: ProgressionCompletion
}

type OutsourcingProgressionStepsDetails = Record<
  JobProgressionStepStatus,
  ProgressionStepDetails | undefined
>

const outsourcingProgressionStepsDetails: OutsourcingProgressionStepsDetails = {
  [JobProgressionStepStatus.NOT_STARTED]: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 20 },
  },
  [JobProgressionStepStatus.OPEN_ITEMS]: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue preparation.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 0, pending: 20 },
  },
  [JobProgressionStepStatus.JOB_STARTED]: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items. <br/> <br/>Once you've completed the deliverable, request draft approval from your client.",
    clientDescription:
      'A draft return is being prepared. When ready, your Pro will contact you to review.',
    progress: { completed: 33, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_IN_REVIEW]: {
    title: 'Draft review',
    proDescription: 'Please wait for your client to review your deliverable.',
    clientDescription:
      'Your Pro has finalized a draft return for review. Please review and notify your Pro if any changes are necessary.',
    progress: { completed: 66, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_REJECTED]: {
    title: 'Changes requested',
    proDescription:
      "Once you're done making the required changes, request draft approval from your client. If anything is missing to complete job, please report open items.",
    clientDescription:
      'You requested changes to the return. Once revised, your Pro will request another draft approval from you.',
    progress: { completed: 50, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_APPROVED]: {
    title: 'Draft approved',
    proDescription:
      'The client approved the deliverable. <br/> <br/> Check back in with the client to finalize the return and complete the job.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 80, pending: 20 },
  },
  [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: undefined,
  [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: undefined,
  [JobProgressionStepStatus.RETURN_FILED]: undefined,
}

type RetailProgressionStepsDetails = Record<
  JobProgressionStepStatus,
  ProgressionStepDetails | undefined
>

const retailProgressionStepsDetails: RetailProgressionStepsDetails = {
  [JobProgressionStepStatus.NOT_STARTED]: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 20 },
  },
  [JobProgressionStepStatus.OPEN_ITEMS]: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue preparation.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 0, pending: 20 },
  },
  [JobProgressionStepStatus.JOB_STARTED]: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items. <br/> <br/>Once you've completed the deliverable, request draft approval from your client.",
    clientDescription:
      'A draft return is being prepared. When ready, your Pro will contact you to review.',
    progress: { completed: 20, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_IN_REVIEW]: {
    title: 'Draft review',
    proDescription: 'Please wait for your client to review your deliverable.',
    clientDescription:
      'Your Pro has a draft ready for review. Please review and notify your Pro if any changes are necessary.',
    progress: { completed: 40, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_REJECTED]: {
    title: 'Changes requested',
    proDescription:
      "Once you're done making the required changes, request draft approval from your client. If anything is missing to complete job, please report open items.",
    clientDescription:
      'You requested changes to the return. Once revised, your Pro will request another draft approval from you.',
    progress: { completed: 40, pending: 20 },
  },
  [JobProgressionStepStatus.DRAFT_APPROVED]: {
    title: 'Draft approved',
    proDescription:
      'Once you have signed and uploaded <strong>Form 8879</strong>, proceed to the next step.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 50, pending: 20 },
  },
  [JobProgressionStepStatus.AUTHORIZATION_REQUESTED]: {
    title: 'Authorization requested',
    proDescription:
      'Contact the client and share <strong>Form 8879</strong>. Once e-file authorization is complete, move onto the next step.',
    clientDescription: '',
    progress: { completed: 60, pending: 20 },
  },
  [JobProgressionStepStatus.AUTHORIZATION_SIGNED]: {
    title: 'Authorization received',
    proDescription:
      'File the return, and upload both the final return and e-file confirmation to proceed.',
    clientDescription: '',
    progress: { completed: 70, pending: 20 },
  },
  [JobProgressionStepStatus.RETURN_FILED]: {
    title: 'Return filed',
    proDescription:
      'You have filed the return. Check back in with the client to finalize and complete the job.',
    clientDescription: '',
    progress: { completed: 80, pending: 20 },
  },
  [JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED]: undefined,
}
